/**
 * KalkiAction — the single contextual chip that appears under a Krishna
 * message when the orchestrator surfaces one. At most ONE per turn.
 *
 * Rendered variants:
 *   - ask_vedic         → inline birth-detail collector button
 *   - sankalp           → "Make this your Sankalp"
 *   - karma             → "Choose today's Karma"
 *   - prasad            → "Offer Prasad"
 *   - blessing          → "Receive a blessing"
 *   - reflection        → "Reflect for a moment"
 *   - reveal_prediction → "See the timing" (opens Vedic Sky inline)
 *   - why               → "Why?"
 *
 * Also exports VedicSkyCard — the tiny luminous 2–4 planet strip surfaced
 * when the orchestrator attaches `vedic_sky` to a message.
 */
import React from "react";
import { Sparkles, Flower2, BookHeart, MoonStar, Compass, HeartHandshake, Flame } from "lucide-react";

const INDIGO = "#4F46E5";
const FUCHSIA = "#D946EF";
const INK = "#1C1C1C";
const MUTE = "rgba(28,28,28,0.55)";

const KIND_META = {
  ask_vedic:   { icon: MoonStar,        tone: "indigo"  },
  sankalp:     { icon: HeartHandshake,  tone: "indigo"  },
  karma:       { icon: Compass,         tone: "fuchsia" },
  prasad:      { icon: Flower2,         tone: "fuchsia" },
  blessing:    { icon: Sparkles,        tone: "fuchsia" },
  reflection:  { icon: BookHeart,       tone: "indigo"  },
  reveal_prediction: { icon: Flame,     tone: "indigo"  },
  why:         { icon: Sparkles,        tone: "indigo"  },
};

export function KalkiAction({ experience, onAccept, dense = false }) {
  if (!experience) return null;
  const meta = KIND_META[experience.kind] || KIND_META.reflection;
  const Icon = meta.icon;
  const tone = meta.tone === "fuchsia" ? FUCHSIA : INDIGO;

  return (
    <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid={`kalki-chip-${experience.kind}`}>
      <button
        type="button"
        onClick={() => onAccept?.(experience)}
        className="group inline-flex items-center gap-2 rounded-full border transition-all"
        style={{
          padding: dense ? "6px 14px" : "8px 16px",
          borderColor: `${tone}40`,
          background: `${tone}08`,
          color: INK,
          fontSize: dense ? 11 : 12,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          fontWeight: 500,
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = `${tone}18`; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = `${tone}08`; }}
      >
        <Icon className="w-3.5 h-3.5" style={{ color: tone }} />
        <span>{experience.label}</span>
        <span className="opacity-0 group-hover:opacity-70 transition-opacity" style={{ fontSize: 10 }}>→</span>
      </button>
    </div>
  );
}

export function VedicSkyCard({ sky }) {
  if (!sky || !sky.planets || sky.planets.length === 0) return null;
  return (
    <div
      className="mt-3 rounded-2xl px-4 py-3"
      style={{
        background: "linear-gradient(135deg, rgba(79,70,229,0.06), rgba(217,70,239,0.04))",
        border: "1px solid rgba(79,70,229,0.14)",
        backdropFilter: "blur(10px)",
      }}
      data-testid="kalki-vedic-sky"
    >
      <div className="flex items-center gap-2 mb-2">
        <MoonStar className="w-3.5 h-3.5" style={{ color: INDIGO }} />
        <span className="text-[10px] tracking-[0.32em]" style={{ color: INDIGO }}>
          ✦ YOUR VEDIC SKY
        </span>
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-1.5">
        {sky.planets.map((p, i) => (
          <div key={i} className="flex items-baseline gap-1.5">
            <span className="text-[11px] tracking-[0.14em] uppercase" style={{ color: MUTE }}>
              {p.planet}
            </span>
            <span className="text-[13px]" style={{ color: INK, fontWeight: 500 }}>
              {p.sign || "—"}
            </span>
            {p.house && (
              <span className="text-[10px]" style={{ color: MUTE }}>
                · h{p.house}
              </span>
            )}
          </div>
        ))}
        {sky.current_dasha?.lord && (
          <div className="flex items-baseline gap-1.5">
            <span className="text-[11px] tracking-[0.14em] uppercase" style={{ color: MUTE }}>
              Period
            </span>
            <span className="text-[13px]" style={{ color: INK, fontWeight: 500 }}>
              {sky.current_dasha.lord}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default KalkiAction;
